// Calendario de clases en vivo (v5.0.0) — vistas Día / Semana / Mes para todos
// los roles. Datos: GET /live-classes (filtrado por rol en el backend).
// Horario de Ciudad de México (igual que la programación de clases).
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import HierarchyFilters, { EMPTY_HIER, hierParams, hierActive } from "@/components/HierarchyFilters";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, Video, Users } from "lucide-react";

const MX_TZ = "America/Mexico_City";
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const WEEKDAYS_LONG = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 06:00 – 21:00

const STATUS = {
  scheduled: { bg: "#DDE5F7", fg: "#2C49A0", label: "Programada" },
  completed: { bg: "#D8F1E4", fg: "#2A7A36", label: "Completada" },
  cancelled: { bg: "#FBDDE8", fg: "#A52258", label: "Cancelada" },
  expired: { bg: "#E6E8EF", fg: "#5A6178", label: "Finalizada" },
};

// v5.4.0 · Diagnósticos CEP / CAD programados (se muestran como «todo el día»).
const DX_STYLE = { CEP: { bg: "#F7DDEC", fg: "#A52258" }, CAD: { bg: "#D6F1EE", fg: "#12766F" } };
const dxKind = (code) => ((code || "").toUpperCase().startsWith("CAD") ? "CAD" : "CEP");
const DX_ROUTE = { estudiante: "/estudiante/cuestionarios", docente: "/docente/cuestionarios", admin: "/admin/cuestionarios", franquiciado: "/franquiciado/asignar-cuestionarios" };

const pad = (n) => String(n).padStart(2, "0");
const fmtKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseKey = (k) => { const [y, m, d] = k.split("-").map(Number); return new Date(y, m - 1, d); };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const mondayOf = (d) => addDays(d, -((d.getDay() + 6) % 7));

function mxParts(iso) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: MX_TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso));
  const g = (t) => parts.find((p) => p.type === t)?.value;
  return { key: `${g("year")}-${g("month")}-${g("day")}`, hour: Number(g("hour")), minute: g("minute") };
}

export default function CalendarioClases() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || "docente";
  const [classes, setClasses] = useState([]);
  const [dx, setDx] = useState([]);
  const [loading, setLoading] = useState(true);
  const todayKey = mxParts(new Date().toISOString()).key;
  const [mode, setMode] = useState("week"); // day | week | month
  const [cursor, setCursor] = useState(() => parseKey(todayKey));
  const [hier, setHier] = useState(EMPTY_HIER); // v5.6.5 · filtros franquicia/institución/colegio/salón
  const [hierStudentIds, setHierStudentIds] = useState(null);
  const canFilter = role === "admin" || role === "franquiciado";

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/live-classes", { params: canFilter ? hierParams(hier) : {} });
        setClasses(Array.isArray(data) ? data : []);
      } catch (_) {
        setClasses([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [hier]); // eslint-disable-line react-hooks/exhaustive-deps

  // Diagnósticos programados: se filtran por los alumnos que cumplen la jerarquía elegida
  useEffect(() => {
    if (!canFilter || !hierActive(hier)) { setHierStudentIds(null); return; }
    api.get("/users", { params: { role: "estudiante", ...hierParams(hier) } })
      .then((r) => setHierStudentIds(new Set((r.data || []).map((u) => u.id))))
      .catch(() => setHierStudentIds(new Set()));
  }, [hier, canFilter]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(role === "estudiante" ? "/questionnaire-assignments/me" : "/questionnaire-assignments");
        setDx(Array.isArray(data) ? data : []);
      } catch (_) { setDx([]); }
    })();
  }, [role]);

  const dxByDay = useMemo(() => {
    const m = {};
    dx.filter((a) => a.scheduled_for && a.status !== "cancelled" && (!hierStudentIds || hierStudentIds.has(a.student_id))).forEach((a) => {
      const raw = String(a.scheduled_for);
      const key = raw.length === 10 ? raw : mxParts(raw).key;
      (m[key] = m[key] || []).push({ ...a, _key: key });
    });
    return m;
  }, [dx, hierStudentIds]);

  const events = useMemo(() => classes.filter((c) => c.scheduled_at).map((c) => ({ ...c, _p: mxParts(c.scheduled_at) })), [classes]);
  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { (m[e._p.key] = m[e._p.key] || []).push(e); });
    Object.values(m).forEach((a) => a.sort((x, y) => new Date(x.scheduled_at) - new Date(y.scheduled_at)));
    return m;
  }, [events]);

  const move = (dir) => {
    setCursor((c) => {
      if (mode === "day") return addDays(c, dir);
      if (mode === "week") return addDays(c, 7 * dir);
      return new Date(c.getFullYear(), c.getMonth() + dir, 1);
    });
  };
  const goToday = () => setCursor(parseKey(todayKey));

  const openClass = (c) => navigate(`/${role}/clases-en-vivo/${c.id}/sala`);

  const weekStart = mondayOf(cursor);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const title = (() => {
    if (mode === "month") return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    if (mode === "day") return `${WEEKDAYS_LONG[(cursor.getDay() + 6) % 7]} ${cursor.getDate()} de ${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
    const e = addDays(weekStart, 6);
    return `${weekStart.getDate()} ${MONTHS[weekStart.getMonth()].slice(0, 3)} – ${e.getDate()} ${MONTHS[e.getMonth()].slice(0, 3)} ${e.getFullYear()}`;
  })();

  const Chip = ({ e, full = false }) => {
    const st = STATUS[e.status] || STATUS.scheduled;
    return (
      <button
        type="button"
        onClick={() => e.status === "scheduled" ? openClass(e) : null}
        data-testid={`cal-event-${e.id}`}
        title={`${e.title} · ${pad(e._p.hour)}:${e._p.minute} · ${st.label}`}
        className="w-full text-left rounded-lg px-1.5 py-1 text-[10px] font-bold truncate"
        style={{ background: st.bg, color: st.fg, textDecoration: e.status === "cancelled" ? "line-through" : "none" }}
      >
        {pad(e._p.hour)}:{e._p.minute} {e.title}{full ? ` · ${e.duration_min || 60} min` : ""}
      </button>
    );
  };

  const DxChip = ({ a }) => {
    const kind = dxKind(a.template_code);
    const st = DX_STYLE[kind];
    const done = a.status === "completed";
    return (
      <button type="button" onClick={() => navigate(DX_ROUTE[role] || "/")} data-testid={`cal-dx-${a.id}`}
        title={`${a.template_title || a.template_code}${a.student_name ? ` · ${a.student_name}` : ""} · ${done ? "Completado" : "Programado"}`}
        className="w-full text-left rounded-lg px-1.5 py-1 text-[10px] font-bold truncate"
        style={{ background: st.bg, color: st.fg, textDecoration: done ? "line-through" : "none", border: `1px dashed ${st.fg}` }}>
        📝 {a.template_code}{role !== "estudiante" && a.student_name ? ` · ${a.student_name}` : ""}
      </button>
    );
  };

  const monthCells = (() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const start = (first.getDay() + 6) % 7;
    const dim = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const a = [];
    for (let i = 0; i < start; i++) a.push(null);
    for (let d = 1; d <= dim; d++) a.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (a.length % 7) a.push(null);
    return a;
  })();

  const HourGrid = ({ days }) => (
    <div className="overflow-x-auto">
      <div style={{ minWidth: days.length > 1 ? 720 : 0 }}>
        <div className="grid" style={{ gridTemplateColumns: `56px repeat(${days.length}, minmax(0,1fr))` }}>
          <div />
          {days.map((d) => {
            const k = fmtKey(d);
            return (
              <div key={k} className={`text-center py-2 text-[11px] font-extrabold uppercase ${k === todayKey ? "text-[#A43790]" : "text-[#6E7591]"}`}>
                {WEEKDAYS[(d.getDay() + 6) % 7]} {d.getDate()}
              </div>
            );
          })}
          <div className="text-[10px] font-bold text-[#8A91AB] text-right pr-2 pt-1 border-t border-[#D3D9E6]">Todo el día</div>
          {days.map((d) => {
            const k = fmtKey(d);
            return (
              <div key={`dx-${k}`} className="min-h-[30px] border-t border-l border-[#D3D9E6] p-0.5 space-y-0.5">
                {(dxByDay[k] || []).map((a) => <DxChip key={a.id} a={a} />)}
              </div>
            );
          })}
          {HOURS.map((h) => (
            <div key={h} className="contents">
              <div className="text-[10px] font-bold text-[#8A91AB] text-right pr-2 pt-1 border-t border-[#D3D9E6]">{pad(h)}:00</div>
              {days.map((d) => {
                const k = fmtKey(d);
                const ev = (byDay[k] || []).filter((e) => e._p.hour === h);
                return (
                  <div key={`${k}-${h}`} className={`min-h-[44px] border-t border-l border-[#D3D9E6] p-0.5 space-y-0.5 ${k === todayKey ? "bg-[#A43790]/5" : ""}`}>
                    {ev.map((e) => <Chip key={e.id} e={e} full={days.length === 1} />)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const dxUpcoming = Object.values(dxByDay).flat().filter((a) => a.status !== "completed" && a._key >= todayKey).sort((a, b) => a._key.localeCompare(b._key)).slice(0, 6);
  const upcoming = events.filter((e) => e.status === "scheduled" && new Date(e.scheduled_at) >= new Date()).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)).slice(0, 6);

  return (
    <div className="space-y-5" data-testid="calendario-clases">
      {canFilter && <HierarchyFilters value={hier} onChange={setHier} />}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] tracking-[0.2em] uppercase font-extrabold text-[#05A859]">Clases en vivo</p>
          <h1 className="font-brand text-3xl text-[#1F2547] inline-flex items-center gap-2"><CalendarDays className="text-[#05A859]" size={28} /> Calendario</h1>
          <p className="text-sm text-[#6E7591]">Horario de Ciudad de México.</p>
        </div>
        <div className="neo-pressed rounded-2xl p-1 inline-flex" data-testid="cal-modes">
          {[["day", "Día"], ["week", "Semana"], ["month", "Mes"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setMode(k)} data-testid={`cal-mode-${k}`}
              className={`px-4 py-1.5 rounded-xl text-sm font-extrabold transition-all ${mode === k ? "neo-flat text-[#A43790]" : "text-[#6E7591]"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5">
        <div className="xl:col-span-3 neo-card p-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div className="flex items-center gap-2">
              <button onClick={() => move(-1)} data-testid="cal-prev" className="w-9 h-9 grid place-items-center rounded-full neo-flat"><ChevronLeft size={16} /></button>
              <h2 className="font-brand text-lg sm:text-xl text-[#1F2547] text-center" data-testid="cal-title">{title}</h2>
              <button onClick={() => move(1)} data-testid="cal-next" className="w-9 h-9 grid place-items-center rounded-full neo-flat"><ChevronRight size={16} /></button>
            </div>
            <button onClick={goToday} data-testid="cal-today" className="text-xs font-extrabold px-4 py-2 rounded-full neo-flat text-[#A43790]">Hoy</button>
          </div>

          {loading ? <p className="text-sm text-[#6E7591] py-10 text-center">Cargando…</p> : (
            <>
              {mode === "day" && <HourGrid days={[cursor]} />}
              {mode === "week" && <HourGrid days={weekDays} />}
              {mode === "month" && (
                <div>
                  <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                    {WEEKDAYS.map((w) => <div key={w} className="text-center text-[11px] font-extrabold text-[#6E7591] uppercase py-1">{w}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1.5">
                    {monthCells.map((d, i) => {
                      if (!d) return <div key={`b${i}`} className="min-h-[76px]" />;
                      const k = fmtKey(d);
                      const ev = byDay[k] || [];
                      const dxs = dxByDay[k] || [];
                      return (
                        <button key={k} type="button" data-testid={`cal-day-${k}`}
                          onClick={() => { setCursor(d); setMode("day"); }}
                          className={`min-h-[76px] rounded-xl p-1.5 text-left transition-all ${k === todayKey ? "neo-pressed" : "neo-flat"}`}>
                          <span className={`text-xs font-extrabold ${k === todayKey ? "text-[#A43790]" : "text-[#1F2547]"}`}>{d.getDate()}</span>
                          <div className="mt-1 space-y-0.5">
                            {dxs.slice(0, 1).map((a) => <DxChip key={a.id} a={a} />)}
                            {ev.slice(0, dxs.length ? 1 : 2).map((e) => <Chip key={e.id} e={e} />)}
                            {ev.length + dxs.length > 2 && <span className="text-[9px] font-bold text-[#6E7591] pl-1">+{ev.length + dxs.length - 2} más</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-[#D3D9E6] text-[11px] text-[#6E7591]">
                {Object.values(STATUS).map((s) => (
                  <span key={s.label} className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.fg }} /> {s.label}</span>
                ))}
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: DX_STYLE.CEP.fg }} /> Diagnóstico CEP</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: DX_STYLE.CAD.fg }} /> Diagnóstico CAD</span>
              </div>
            </>
          )}
        </div>

        <aside className="neo-card p-4" data-testid="cal-upcoming">
          <h3 className="font-brand text-lg text-[#1F2547] mb-3">Próximas clases</h3>
          {upcoming.length === 0 ? (
            <p className="text-sm text-[#6E7591] py-6 text-center">No hay clases programadas próximamente.</p>
          ) : (
            <div className="space-y-3">
              {upcoming.map((c) => (
                <div key={c.id} className="neo-flat rounded-2xl p-3">
                  <p className="font-bold text-sm text-[#1F2547] break-words">{c.title}</p>
                  <p className="text-xs text-[#6E7591] inline-flex items-center gap-1 mt-0.5"><Clock size={11} /> {c._p.key} · {pad(c._p.hour)}:{c._p.minute} · {c.duration_min || 60} min</p>
                  {(c.student_summary?.length > 0 || c.student_ids?.length > 0) && (
                    <p className="text-xs text-[#6E7591] inline-flex items-center gap-1 mt-0.5 ml-2"><Users size={11} /> {c.student_summary?.length || c.student_ids?.length}</p>
                  )}
                  <button onClick={() => openClass(c)} className="mt-2 w-full jasq-btn-primary text-xs inline-flex items-center justify-center gap-1.5 py-1.5"><Video size={12} /> Abrir sala</button>
                </div>
              ))}
            </div>
          )}
          <h3 className="font-brand text-lg text-[#1F2547] mt-5 mb-3">Diagnósticos programados</h3>
          {dxUpcoming.length === 0 ? (
            <p className="text-sm text-[#6E7591] py-3 text-center" data-testid="cal-dx-vacio">No hay CEP / CAD programados.</p>
          ) : (
            <div className="space-y-2" data-testid="cal-dx-lista">
              {dxUpcoming.map((a) => (
                <button key={a.id} type="button" onClick={() => navigate(DX_ROUTE[role] || "/")} className="w-full text-left neo-flat rounded-2xl p-3">
                  <p className="font-bold text-sm text-[#1F2547]">{a.template_code}{role !== "estudiante" && a.student_name ? ` · ${a.student_name}` : ""}</p>
                  <p className="text-xs text-[#6E7591]">{a._key}{a.template_title ? ` · ${a.template_title}` : ""}</p>
                </button>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
